import reactUpdate from 'immutability-helper';
import React from 'react';
import _ from 'underscore';

import * as OrgActions from 'actions/OrganizationActions';
import DataConnector from 'connect/DataConnector';
import ContinueButton from 'nav/full-screen/ContinueButton';
import EntityStore from 'stores/EntityStore';
import Col from 'ui-library/bs/Col';
import Row from 'ui-library/bs/Row';

// @ts-expect-error
import EntityList from './EntityList';
import RegistryActivationFlowStore from './RegistryActivationFlowStore';

const ONLY_LETTERS = /^[a-zA-Z]+$/;

function getInitials({initials, name}: {initials?: string; name?: string}): string {
  if (initials) {
    return initials;
  }
  if (name) {
    const nameParts = name.split(' ');
    return nameParts.map((np) => np.charAt(0).toUpperCase()).join('');
  }
  return '';
}

class MembersScreenConnector extends DataConnector<Props, EnhancedProps> {
  shouldSubscribeToRedux = false;
  stores = [EntityStore, RegistryActivationFlowStore];

  enhanceProps(): EnhancedProps {
    const orgId = this.registryActivationFlowStore().getOrganizationId();
    const membersTemp = this.entityStore().getMembersForGroup(orgId ?? '') || [];
    const membersFromStore = membersTemp.map((member) => {
      const initials = getInitials(member);
      return {
        handle: member.handle,
        id: member.id,
        initials,
        name: member.name,
      };
    });
    return {membersFromStore};
  }
}

interface Props {
  onContinue: () => void;
}
interface EnhancedProps {
  membersFromStore: Array<{handle: string; id: string; initials: string; name: string}>;
}

interface State {
  organization: {
    id: string;
    handle: string;
    initials: string;
    name: string;
  };
  members: Array<{handle: string; id: string; initials: string; name: string}>;
}

class MembersScreen extends React.Component<Props & EnhancedProps, State> {
  constructor(props: Props & EnhancedProps) {
    super(props);
    const orgId = RegistryActivationFlowStore.getOrganizationId();
    const org = EntityStore.getEntity(orgId ?? '')!;

    this.state = {
      organization: {
        id: org.id,
        handle: org.handle,
        initials: getInitials(org),
        name: org.name,
      },
      members: props.membersFromStore,
    };
  }

  componentDidMount(): void {
    OrgActions.fetchMembers(RegistryActivationFlowStore.getOrganizationId() ?? '');
  }

  componentDidUpdate(prevProps: Props & EnhancedProps): void {
    if (prevProps.membersFromStore !== this.props.membersFromStore) {
      this.setState({members: this.props.membersFromStore});
    }
  }

  _handleOrgChange = (value: string): void => {
    this.setState({
      organization: reactUpdate(this.state.organization, {initials: {$set: value}}),
    });
  };

  _handleMemberChange = (value: string, index: number): void => {
    this.setState({
      members: reactUpdate(this.state.members, {[index]: {initials: {$set: value}}}),
    });
  };

  _getInitialsErrorOrg = (initials: string): string | null => this._getInitialsError(initials);

  _getInitialsErrorMember = (initials: string): string | null => {
    const error = this._getInitialsError(initials);
    if (error) {
      return error;
    }
    // eslint-disable-next-line no-restricted-syntax
    const sameInitials = _.filter(
      this.state.members,
      (member) => member.initials.toLowerCase() === initials.toLowerCase()
    );
    if (sameInitials.length > 1) {
      return 'Member initials must be unique';
    }
    return null;
  };

  _getInitialsError = (initials: string): string | null => {
    if (!initials) {
      return 'Required';
    }
    if (!ONLY_LETTERS.test(initials)) {
      return 'Only letters are allowed';
    }
    return null;
  };

  _isSubmissionInvalid = (): boolean => {
    if (
      this._getInitialsError(this.state.organization.initials) ||
      this.state.members.some((member) => this._getInitialsErrorMember(member.initials))
    ) {
      return true;
    }
    return false;
  };

  _setInitials = (): void => {
    const initialsByUserId: {[userId: string]: string} = {};
    this.state.members.forEach((member) => {
      initialsByUserId[member.id] = member.initials;
    });
    OrgActions.setInitials(
      this.state.organization.id,
      {
        orgInitials: this.state.organization.initials,
        initialsByUserId,
      },
      false
    );
    this.props.onContinue();
  };

  render(): JSX.Element {
    return (
      <div className="registryActivation-container membersScreen-main">
        <div className="membersScreen-text-container">
          <h1>Set up your organization's members</h1>
          <p>Organizations allow team members to easily share and collaborate in the Bioregistry.</p>
          <p>
            Each organization and member requires their own unique set of initials. Add initials for them to
            the right.
          </p>
        </div>
        <div className="membersScreen-table-container">
          <div className="u-spacing-top membersScreen-list">
            <Row className="membersScreen-list-header">
              <Col sm={4}>Handle</Col>
              <Col sm={4}>Name</Col>
              <Col sm={4}>Initials</Col>
            </Row>
            <div className="membersScreen-list-container">
              <EntityList
                entities={[this.state.organization]}
                onChange={this._handleOrgChange}
                type="Organization"
                validate={this._getInitialsErrorOrg}
              />
              <EntityList
                entities={this.state.members}
                onChange={this._handleMemberChange}
                type="User"
                validate={this._getInitialsErrorMember}
              />
            </div>
          </div>
          <div className="registryActivation-button-container">
            <ContinueButton
              text="Complete setup"
              onClick={this._setInitials}
              primary={true}
              disabled={this._isSubmissionInvalid() || this.state.members.length === 0}
            />
          </div>
        </div>
      </div>
    );
  }
}

export default MembersScreenConnector.connect(MembersScreen);
